// WP-4.2 (AU-7/AM-3/TX-4/LG-2/LG-3, S-10 action menu): mutation hooks for the transaction
// detail page's Correct-amount / Relink-account / Split-lines actions, calling the
// engine-backed correction routes added in the previous phase
// (apps/api/src/routes/corrections.ts). Correct-party has no S-10 action-menu UI yet (out of
// this phase's scope — see TransactionDetail.tsx's own header comment), so no hook for it
// lives here; add one alongside a UI consumer if/when that lands.
//
// Mirrors the invalidate-on-success pattern every other mutation in this codebase already
// uses (see useReverseTransaction/useRelinkTransactionToProject in transactions.ts) —
// corrections can move balances (amount/account changes) the same way a reversal does, so
// both query keys invalidate, same as useReverseTransaction's own invalidate.
import {
  correctAmountResponseSchema,
  relinkAccountResponseSchema,
  splitLinesResponseSchema,
  type CorrectAmountResponse,
  type RelinkAccountResponse,
  type SplitLinesRequest,
  type SplitLinesResponse,
} from '@pfos/shared';
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { api } from './api';
import { ACCOUNTS_KEY } from './accounts';
import { TRANSACTIONS_KEY } from './transactions';

function useInvalidateTransactions(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: TRANSACTIONS_KEY }),
      queryClient.invalidateQueries({ queryKey: ACCOUNTS_KEY }), // balances may have changed too
    ]);
  };
}

// AU-7: "actually 550" — the web equivalent action, S-10's "Correct" menu entry.
export function useCorrectTransactionAmount(
  id: string,
): UseMutationResult<CorrectAmountResponse, Error, number> {
  const invalidate = useInvalidateTransactions();
  return useMutation({
    mutationFn: async (amountMinor: number) =>
      correctAmountResponseSchema.parse(
        await api.post(`/api/v1/transactions/${id}/correct-amount`, { amountMinor }),
      ),
    onSuccess: invalidate,
  });
}

// AM-3 "changes to financial obligations" / "uncertain relinking" — S-10's "Relink" menu entry.
export function useRelinkTransactionAccount(
  id: string,
): UseMutationResult<RelinkAccountResponse, Error, string> {
  const invalidate = useInvalidateTransactions();
  return useMutation({
    mutationFn: async (accountId: string) =>
      relinkAccountResponseSchema.parse(
        await api.post(`/api/v1/transactions/${id}/relink-account`, { accountId }),
      ),
    onSuccess: invalidate,
  });
}

// TX-4: S-10's "Split" menu entry — WEB-ONLY (see splitTransactionLines's own header comment
// in ledger.ts). The Σlines === current-amount invariant is enforced both client-side (before
// this ever fires, mirroring TransactionForm's own create-time split validation) and again by
// the engine (AR-3 source of truth).
export function useSplitTransactionLines(
  id: string,
): UseMutationResult<SplitLinesResponse, Error, SplitLinesRequest['lines']> {
  const invalidate = useInvalidateTransactions();
  return useMutation({
    mutationFn: async (lines: SplitLinesRequest['lines']) =>
      splitLinesResponseSchema.parse(
        await api.post(`/api/v1/transactions/${id}/split-lines`, { lines }),
      ),
    onSuccess: invalidate,
  });
}
